import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { Plus, Package, Edit2, Trash2, Upload, Download, FileSpreadsheet, AlertTriangle, Search, ChevronDown, ChevronRight, Truck, Factory, Clock, X, Settings, Library, Sparkles, Image } from 'lucide-react';
import { useCategories, CATEGORY_OPTIONS as STATIC_CATEGORY_OPTIONS, getCategoryColor } from '@/lib/categories';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category colors - includes all categories + legacy support
const categoryColors = {
  FNG: 'bg-green-100 text-green-800 border-green-200',
  RMT: 'bg-blue-100 text-blue-800 border-blue-200',
  SFG: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  CTM: 'bg-purple-100 text-purple-800 border-purple-200',
  ASSET: 'bg-orange-100 text-orange-800 border-orange-200',
  // Legacy support
  finished_goods: 'bg-green-100 text-green-800 border-green-200',
  raw_material: 'bg-blue-100 text-blue-800 border-blue-200',
  semi_finished: 'bg-yellow-100 text-yellow-800 border-yellow-200'
};

// Category labels - for display
const categoryLabels = {
  FNG: 'FNG',
  RMT: 'RMT',
  SFG: 'SFG',
  CTM: 'CTM',
  ASSET: 'ASSET',
  // Legacy support
  finished_goods: 'FNG',
  raw_material: 'RMT',
  semi_finished: 'SFG'
};

// Unit options will be fetched from API

const needTypeColors = {
  APO: 'bg-orange-100 text-orange-700 border-orange-200',
  REG: 'bg-blue-100 text-blue-700 border-blue-200'
};

const sourceTypeIcons = {
  MP: <Truck className="w-3 h-3" />,
  IP: <Factory className="w-3 h-3" />
};

export default function ItemMasterPage() {
  const { user, getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // MP, IP, or all
  const [needFilter, setNeedFilter] = useState('all'); // APO, REG, or all
  const [locationFilter, setLocationFilter] = useState('all'); // location_id or all
  const [open, setOpen] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null); // Single expanded item (accordion style)
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const [uomOptions, setUomOptions] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const fileInputRef = useRef(null);
  
  // Use centralized categories
  const { categoryOptions, isManufacturable, validCodes: VALID_CATEGORIES } = useCategories(getAuthHeader);
  // Use categoryOptions from hook, fallback to static for initial render
  const CATEGORY_OPTIONS = categoryOptions.length > 0 ? categoryOptions : STATIC_CATEGORY_OPTIONS;
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'RMT',  // Default to RMT
    description: '',
    image_url: '',
    unit: '',
    min_stock_level: 0,
    max_stock_level: 0,
    reorder_quantity: 0,
    opening_stock: 0,
    opening_stock_price: 0,
    opening_bin_card_stock: 0,
    need_type: 'REG',
    lead_time: 0,
    source_type: 'MP',
    default_location_id: '',
    sub_location_id: '',
    specs: '',
    classification: '',  // Mandatory for all items
    generated_code_id: null,  // Links to temp_item_codes.id if imported from Code Library
    bom_id: '',  // Links FNG/SFG item to a BOM
    is_variant_template: false,  // True for template items (e.g., "CHAIR CLARK DL DL")
    // ASSET-specific fields
    asset_status: 'In Use',
    asset_purchase_date: '',
    asset_purchase_value: 0,
    asset_purchase_ref: ''
  });
  
  // Classification options for all items
  const [classifications, setClassifications] = useState([]);
  const [openClassificationDialog, setOpenClassificationDialog] = useState(false);
  const [newClassification, setNewClassification] = useState('');
  const [classificationSearch, setClassificationSearch] = useState('');

  // Code Library Picker (for FNG items)
  const [codePickerOpen, setCodePickerOpen] = useState(false);
  const [availableCodes, setAvailableCodes] = useState([]);
  const [codePickerSearch, setCodePickerSearch] = useState('');
  const [codePickerLoading, setCodePickerLoading] = useState(false);
  
  // Available BOMs (for FNG/SFG items)
  const [availableBoms, setAvailableBoms] = useState([]);
  const [bomSearch, setBomSearch] = useState('');

  // Convert Google Drive sharing URL to direct image URL
  // Note: Google Drive images may still have CORS issues. For best results, use:
  // 1. Direct image URLs (e.g., from your own server)
  // 2. Image hosting services (imgur, cloudinary, etc.)
  // 3. File upload option below
  const convertGoogleDriveUrl = (url) => {
    if (!url) return url;
    
    // Match Google Drive file patterns
    // Format 1: https://drive.google.com/file/d/FILE_ID/view...
    const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fileMatch) {
      // Use thumbnail endpoint which has better CORS support
      return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w400`;
    }
    
    // Format 2: https://drive.google.com/open?id=FILE_ID
    const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
    if (openMatch) {
      return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w400`;
    }
    
    // Format 3: https://drive.google.com/uc?id=FILE_ID or export=view
    const ucMatch = url.match(/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/);
    if (ucMatch) {
      return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w400`;
    }
    
    // Return as is for other URLs
    return url;
  };

  // Handle image URL change with Google Drive conversion
  const handleImageUrlChange = (url) => {
    const convertedUrl = convertGoogleDriveUrl(url);
    setFormData({ ...formData, image_url: convertedUrl });
  };

  // Helper to convert old dict-based specs to string (backward compatibility)
  const normalizeSpecs = (specs) => {
    if (!specs) return '';
    if (typeof specs === 'string') return specs;
    // Convert old dict format to string
    if (typeof specs === 'object') {
      const lines = [];
      Object.entries(specs).forEach(([key, value]) => {
        if (/^\d+$/.test(key)) {
          lines.push(String(value));
        } else {
          lines.push(`${key}: ${value}`);
        }
      });
      return lines.join('\n');
    }
    return '';
  };

  const fetchItems = async () => {
    try {
      // Include image_url for Item Master page (needed for editing items with images)
      const response = await axios.get(`${API_URL}/items?include_image=true`, { headers: getAuthHeader() });
      setItems(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      toast.error('Failed to fetch items');
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`, { headers: getAuthHeader() });
      setLocations(response.data.filter(l => l.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchSubLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/sub-locations`, { headers: getAuthHeader() });
      setSubLocations(response.data.filter(sl => sl.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch sub-locations:', error);
    }
  };
  
  // Fetch classification options for FNG items
  const fetchClassifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/items/classifications`, { headers: getAuthHeader() });
      setClassifications(response.data.classifications || []);
    } catch (error) {
      console.error('Failed to fetch classifications:', error);
    }
  };

  // Fetch UOM options from API
  const fetchUomOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/uom/options`, { headers: getAuthHeader() });
      setUomOptions(response.data.codes || []);
    } catch (error) {
      console.error('Failed to fetch UOM options:', error);
      // Fallback to common UOMs if API fails
      setUomOptions(['NOS', 'KG', 'MTR', 'SQFT', 'LTR', 'SET']);
    }
  };

  // Fetch available BOMs for FNG/SFG items
  const fetchBoms = async () => {
    try {
      const response = await axios.get(`${API_URL}/bom`, { headers: getAuthHeader() });
      setAvailableBoms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch BOMs:', error);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchLocations();
    fetchSubLocations();
    fetchClassifications();
    fetchUomOptions();
    fetchBoms();
  }, []);

  // Filter items based on selected filters (client-side filtering for master data)
  useEffect(() => {
    let result = items;
    
    if (selectedCategory !== 'all') {
      // Simple uppercase comparison - all categories stored as uppercase in DB
      result = result.filter(item => item.category?.toUpperCase() === selectedCategory.toUpperCase());
    }
    
    // Source filter (MP/IP)
    if (sourceFilter !== 'all') {
      result = result.filter(item => item.source_type === sourceFilter);
    }
    
    // Need filter (APO/REG)
    if (needFilter !== 'all') {
      result = result.filter(item => (item.need_type || 'REG') === needFilter);
    }
    
    // Location filter
    if (locationFilter !== 'all') {
      result = result.filter(item => item.default_location_id === locationFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.item_code?.toLowerCase().includes(term)
      );
    }
    
    setFilteredItems(result);
  }, [items, selectedCategory, sourceFilter, needFilter, locationFilter, searchTerm]);

  // Fetch available codes from Code Library (for FNG items)
  const fetchAvailableCodes = async (search = '') => {
    setCodePickerLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '100');
      
      const response = await axios.get(
        `${API_URL}/item-code-generator/codes/available-for-import?${params}`,
        { headers: getAuthHeader() }
      );
      setAvailableCodes(response.data.codes || []);
    } catch (error) {
      console.error('Error fetching available codes:', error);
      toast.error('Failed to load available codes');
    } finally {
      setCodePickerLoading(false);
    }
  };

  // Handle importing a code from the library
  const handleImportCode = (code) => {
    // Auto-fill name and specs from the generated code
    setFormData(prev => ({
      ...prev,
      name: code.code,  // The full generated code becomes the item name
      specs: code.specs || '',  // Generated specs
      generated_code_id: code.id  // Link to the generated code
    }));
    setCodePickerOpen(false);
    toast.success('Code imported! Name and specs have been auto-filled.');
  };

  // Open code picker
  const openCodePicker = () => {
    setCodePickerSearch('');
    fetchAvailableCodes('');
    setCodePickerOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate classification is provided
    if (!formData.classification || !formData.classification.trim()) {
      toast.error('Classification is mandatory');
      return;
    }
    
    // Validate lead_time is provided and > 0
    if (!formData.lead_time || parseInt(formData.lead_time) <= 0) {
      toast.error('Lead Time is mandatory and must be greater than 0');
      return;
    }
    
    // Bug B: UOM is mandatory — no silent 'NOS' default at backend anymore.
    if (!formData.unit || !String(formData.unit).trim()) {
      toast.error('Unit of Measurement is mandatory. Please select a UOM.');
      return;
    }
    
    // Validate default_location_id is mandatory for MP (Market Purchase) items
    if (formData.source_type === 'MP' && !formData.default_location_id) {
      toast.error('Default Location is mandatory for Market Purchase (MP) items');
      return;
    }
    
    try {
      const payload = {
        name: formData.name,
        category: formData.category.toUpperCase(),  // Ensure uppercase
        description: formData.description,
        image_url: formData.image_url,
        unit: formData.unit,
        min_stock_level: parseFloat(formData.min_stock_level) || 0,
        max_stock_level: parseFloat(formData.max_stock_level) || 0,
        reorder_quantity: parseFloat(formData.reorder_quantity) || 0,
        lead_time: parseInt(formData.lead_time) || 0,
        need_type: formData.need_type,
        source_type: formData.source_type,
        default_location_id: formData.default_location_id || null,
        sub_location_id: formData.sub_location_id || null,
        specs: formData.specs ? formData.specs.toUpperCase() : '',
        classification: formData.classification.trim().toUpperCase()  // Mandatory for all items
      };
      
      // Add BOM linking for manufacturable items (FNG/SFG)
      if (isManufacturable(formData.category)) {
        payload.bom_id = formData.bom_id || null;
        payload.is_variant_template = formData.is_variant_template || false;
      }
      
      // Users with item_master module access can set/update opening stock
      const hasItemMasterAccess = user?.allowed_modules?.includes('item_master') || user?.is_owner;
      if (hasItemMasterAccess) {
        payload.opening_stock = parseFloat(formData.opening_stock) || 0;
        payload.opening_stock_price = parseFloat(formData.opening_stock_price) || 0;
        payload.opening_bin_card_stock = parseFloat(formData.opening_bin_card_stock) || 0;
      }

      if (editItem) {
        await axios.patch(`${API_URL}/items/${editItem.id}`, payload, { headers: getAuthHeader() });
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API_URL}/items`, payload, { headers: getAuthHeader() });
        toast.success('Item created successfully');
      }
      setOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to save item'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'RMT',
      description: '',
      image_url: '',
      unit: '',
      min_stock_level: 0,
      max_stock_level: 0,
      reorder_quantity: 0,
      opening_stock: 0,
      opening_stock_price: 0,
      opening_bin_card_stock: 0,
      need_type: 'REG',
      lead_time: 0,
      source_type: 'MP',
      default_location_id: '',
      sub_location_id: '',
      specs: '',
      classification: '',
      bom_id: '',
      is_variant_template: false
    });
    setEditItem(null);
    setBomSearch('');
  };

  // Normalize category to new format
  const normalizeCategory = (cat) => {
    const mapping = {
      'raw_material': 'RMT',
      'finished_goods': 'FNG',
      'semi_finished': 'SFG'
    };
    return mapping[cat] || cat?.toUpperCase() || 'RMT';
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      name: item.name,
      category: normalizeCategory(item.category),
      description: item.description || '',
      image_url: item.image_url || '',
      unit: item.unit || 'NOS',
      min_stock_level: item.min_stock_level || 0,
      max_stock_level: item.max_stock_level || 0,
      reorder_quantity: item.reorder_quantity || 0,
      opening_stock: item.opening_stock || 0,
      opening_stock_price: item.opening_stock_price || 0,
      opening_bin_card_stock: item.opening_bin_card_stock || item.bin_card_stock || 0,
      need_type: item.need_type || 'REG',
      lead_time: item.lead_time || 0,
      source_type: item.source_type || 'MP',
      default_location_id: item.default_location_id || '',
      sub_location_id: item.sub_location_id || '',
      specs: normalizeSpecs(item.specs),
      classification: item.classification || '',
      bom_id: item.bom_id || '',
      is_variant_template: item.is_variant_template || false,
      // ASSET-specific fields
      asset_status: item.asset_status || 'In Use',
      asset_purchase_date: item.asset_purchase_date || '',
      asset_purchase_value: item.asset_purchase_value || 0,
      asset_purchase_ref: item.asset_purchase_ref || ''
    });
    setBomSearch('');
    setOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/items/${selectedItem.id}`, { headers: getAuthHeader() });
      toast.success('Item deleted successfully');
      setOpenDelete(false);
      fetchItems();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to delete item'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    }
  };

  const openDeleteDialog = (item) => {
    setSelectedItem(item);
    setOpenDelete(true);
  };

  const toggleExpand = (itemId) => {
    setExpandedItem(prev => prev === itemId ? null : itemId);
  };

  // File upload handlers
  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/items/template/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'item_master_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/items/bulk-upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadResult(response.data);
      toast.success(`Upload complete: ${response.data.created || 0} items created`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      fetchItems();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Upload failed';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
      setUploadResult({ errors: [errorMessage] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Item Master</h1>
            <p className="text-muted-foreground">Manage inventory items and their attributes</p>
          </div>
          <div className="flex gap-2">
            {/* Bulk Upload Dialog */}
            <Dialog open={openUpload} onOpenChange={setOpenUpload}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Upload
                </Button>
              </DialogTrigger>
              {openUpload && (
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5" />
                    Bulk Upload Items
                  </DialogTitle>
                  <DialogDescription>
                    Upload items from an Excel file
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Step 1: Download Template</p>
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                      <Download className="w-4 h-4 mr-2" />
                      Download Excel Template
                    </Button>
                  </div>
                  
                  <div className="p-4 border-2 border-dashed rounded-lg">
                    <p className="text-sm font-medium mb-2">Step 2: Upload Filled File</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                      disabled={uploading}
                    />
                    {uploading && (
                      <p className="text-sm text-muted-foreground mt-2">Processing...</p>
                    )}
                  </div>
                  
                  {uploadResult && (
                    <div className={`p-4 rounded-lg ${uploadResult.errors?.length > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                      <p className="font-medium mb-2">Upload Results:</p>
                      <p className="text-sm text-green-700">✓ {uploadResult.created || 0} items created</p>
                      {uploadResult.errors?.length > 0 && (
                        <>
                          <p className="text-sm text-yellow-700 mt-1">⚠ {uploadResult.errors.length} errors</p>
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            {uploadResult.errors?.slice(0, 10).map((err, i) => (
                              <p key={i} className="text-xs text-red-600">{err}</p>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setOpenUpload(false); setUploadResult(null); }}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
              )}
            </Dialog>
            
            {/* Create Button */}
            <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Item
                </Button>
              </DialogTrigger>
              {open && (
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editItem ? 'Edit' : 'Create'} Item</DialogTitle>
                  <DialogDescription>
                    {editItem ? 'Update item details' : 'Add a new item to the inventory'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <div className="grid grid-cols-3 gap-4 py-4">
                    {/* Import from Code Library - Only for FNG category and new items */}
                    {formData.category === 'FNG' && !editItem && (
                      <div className="col-span-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium">Import from Code Library</span>
                            <span className="text-xs text-muted-foreground">Auto-fill name & specs from generated codes</span>
                          </div>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={openCodePicker}
                            className="gap-2"
                          >
                            <Library className="w-4 h-4" />
                            Browse Codes
                          </Button>
                        </div>
                        {formData.generated_code_id && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Imported from Code Library
                          </p>
                        )}
                      </div>
                    )}

                    {/* Basic Info */}
                    <div className="col-span-2 space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                        placeholder={formData.category === 'FNG' ? "Import from Code Library or enter manually" : "e.g., STEEL SHEET 2MM"}
                        required
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val, generated_code_id: null })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">CTM items use only bin card stock</p>
                    </div>
                    
                    {/* BOM Linking - Always visible, disabled for RMT/CTM */}
                    <div className="space-y-2">
                      <Label className={!['FNG', 'SFG'].includes(formData.category) ? 'text-muted-foreground' : ''}>
                        Link to BOM
                      </Label>
                      <Select 
                        value={formData.bom_id || ''} 
                        onValueChange={(val) => setFormData({ ...formData, bom_id: val === 'none' ? '' : val })}
                        disabled={!['FNG', 'SFG'].includes(formData.category)}
                      >
                        <SelectTrigger className={!['FNG', 'SFG'].includes(formData.category) ? 'opacity-50 cursor-not-allowed' : ''}>
                          <SelectValue placeholder={['FNG', 'SFG'].includes(formData.category) ? "Select BOM (optional)" : "N/A for RMT/CTM"} />
                        </SelectTrigger>
                        <SelectContent>
                          <div className="px-2 pb-2">
                            <Input
                              placeholder="Search BOM..."
                              value={bomSearch}
                              onChange={(e) => setBomSearch(e.target.value)}
                              className="h-8"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <SelectItem value="none">-- No BOM --</SelectItem>
                          {availableBoms
                            .filter(bom => !bomSearch || 
                              bom.bom_name?.toLowerCase().includes(bomSearch.toLowerCase()) ||
                              bom.bom_code?.toLowerCase().includes(bomSearch.toLowerCase()))
                            .slice(0, 50)
                            .map(bom => (
                              <SelectItem key={bom.id} value={bom.id}>
                                {bom.bom_name || bom.bom_code || 'Unnamed BOM'}
                              </SelectItem>
                            ))}
                          {availableBoms.filter(bom => !bomSearch || 
                            bom.bom_name?.toLowerCase().includes(bomSearch.toLowerCase()) ||
                            bom.bom_code?.toLowerCase().includes(bomSearch.toLowerCase())).length === 0 && (
                            <div className="py-2 px-2 text-sm text-muted-foreground text-center">
                              No BOMs found
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {['FNG', 'SFG'].includes(formData.category) 
                          ? 'Link this item to a BOM for Work Order creation'
                          : 'BOM linking is only available for FNG/SFG items'}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Unit <span className="text-red-500">*</span></Label>
                      <Select value={formData.unit} onValueChange={(val) => setFormData({ ...formData, unit: val })} data-testid="item-master-unit-select">
                        <SelectTrigger data-testid="item-master-unit-trigger">
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {uomOptions.map(unit => (
                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1"></div>
                    
                    {/* Product Image */}
                    <div className="col-span-3 pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3">Product Image</p>
                    </div>
                    <div className="col-span-3 space-y-3">
                      <div className="flex gap-4">
                        {/* Image Preview */}
                        <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                          {formData.image_url ? (
                            <img 
                              src={formData.image_url} 
                              alt="Product" 
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <Image className="w-8 h-8 text-muted-foreground" />
                          )}
                        </div>
                        
                        {/* Upload Options */}
                        <div className="flex-1 space-y-3">
                          <div className="space-y-2">
                            <Label className="text-xs">Image URL</Label>
                            <div className="flex gap-2">
                              <Input
                                value={formData.image_url || ''}
                                onChange={(e) => handleImageUrlChange(e.target.value)}
                                placeholder="Paste direct image URL or Google Drive link"
                                className="flex-1"
                              />
                              {formData.image_url && (
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setFormData({ ...formData, image_url: '' })}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-xs text-muted-foreground">
                            OR upload an image file (recommended for best compatibility):
                          </div>
                          
                          <div>
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.size > 2 * 1024 * 1024) {
                                    toast.error('Image must be less than 2MB');
                                    return;
                                  }
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setFormData({ ...formData, image_url: reader.result });
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              className="text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Max 2MB. JPG, PNG, WEBP supported.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stock Levels */}
                    <div className="col-span-3 pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3">Stock Levels</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Min Stock Level</Label>
                      <Input
                        type="number"
                        value={formData.min_stock_level}
                        onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Stock Level</Label>
                      <Input
                        type="number"
                        value={formData.max_stock_level}
                        onChange={(e) => setFormData({ ...formData, max_stock_level: e.target.value })}
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Reorder Quantity</Label>
                      <Input
                        type="number"
                        value={formData.reorder_quantity}
                        onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })}
                        min="0"
                      />
                    </div>
                    
                    {/* Opening Stock - Editable by anyone with Item Master access */}
                    <div className="col-span-3 pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3">
                        Opening Stock
                      </p>
                      <p className="text-xs text-muted-foreground">Sets the initial Stock in Hand and Bin Card values</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Opening Stock Qty (System)</Label>
                      <Input
                        type="number"
                        value={formData.opening_stock}
                        onChange={(e) => setFormData({ ...formData, opening_stock: e.target.value })}
                        min="0"
                        placeholder="Initial quantity"
                      />
                      <p className="text-xs text-muted-foreground">Stock in Hand (financially accounted)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Opening Bin Card Stock</Label>
                      <Input
                        type="number"
                        value={formData.opening_bin_card_stock}
                        onChange={(e) => setFormData({ ...formData, opening_bin_card_stock: e.target.value })}
                        min="0"
                        placeholder="Bin card quantity"
                      />
                      <p className="text-xs text-muted-foreground">Physical stock in store bin card</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Opening Stock Price</Label>
                      <Input
                        type="number"
                        value={formData.opening_stock_price}
                        onChange={(e) => setFormData({ ...formData, opening_stock_price: e.target.value })}
                        min="0"
                        step="0.01"
                        placeholder="Price per unit"
                      />
                    </div>
                    
                    {/* Item Classification */}
                    <div className="col-span-3 pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3">Item Classification</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Classification *</Label>
                        {(user?.is_owner || user?.allowed_modules?.includes('item_master')) && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 text-xs"
                            onClick={() => setOpenClassificationDialog(true)}
                          >
                            <Settings className="h-3 w-3 mr-1" />
                            Manage
                          </Button>
                        )}
                      </div>
                      <SearchableSelect
                        options={[...classifications]
                          .sort((a, b) => a.localeCompare(b))
                          .map(c => ({ value: c, label: c }))}
                        value={formData.classification || ''}
                        onValueChange={(val) => setFormData({ ...formData, classification: val })}
                        placeholder="Select classification..."
                        searchPlaceholder="Search classifications..."
                        emptyText="No classifications found"
                      />
                      <p className="text-xs text-muted-foreground">Mandatory - used for grouping in reports</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Opening Stock Value</Label>
                      <div className="h-10 px-3 py-2 bg-muted rounded-md text-sm flex items-center">
                        ₹{((parseFloat(formData.opening_stock) || 0) * (parseFloat(formData.opening_stock_price) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                    
                    {/* ASSET-specific fields - only show when category is ASSET */}
                    {formData.category === 'ASSET' && (
                      <>
                        <div className="col-span-3 pt-2 border-t">
                          <p className="text-sm font-medium text-orange-600 mb-3">Asset Information</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Purchase Date</Label>
                          <Input
                            type="date"
                            value={formData.asset_purchase_date || ''}
                            onChange={(e) => setFormData({ ...formData, asset_purchase_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Purchase Value (₹)</Label>
                          <Input
                            type="number"
                            value={formData.asset_purchase_value || ''}
                            onChange={(e) => setFormData({ ...formData, asset_purchase_value: e.target.value })}
                            min="0"
                            step="0.01"
                            placeholder="Purchase price"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Purchase Reference</Label>
                          <Input
                            value={formData.asset_purchase_ref || ''}
                            onChange={(e) => setFormData({ ...formData, asset_purchase_ref: e.target.value })}
                            placeholder="PO number or reference"
                          />
                        </div>
                        <div className="col-span-3 p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
                          <p>Assets have a simplified workflow: Purchase → In Use → Sold/Disposed</p>
                          <p className="text-xs mt-1">View and manage assets in the Asset Register page</p>
                        </div>
                      </>
                    )}
                    
                    {/* Purchase Planning */}
                    <div className="col-span-3 pt-2 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3">Purchase Planning</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Need Type</Label>
                      <Select value={formData.need_type} onValueChange={(val) => setFormData({ ...formData, need_type: val })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="REG">REG (Regular)</SelectItem>
                          <SelectItem value="APO">APO (As Per Order)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">APO = ordered only when needed for specific orders</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Lead Time (days) *</Label>
                      <Input
                        type="number"
                        value={formData.lead_time}
                        onChange={(e) => setFormData({ ...formData, lead_time: e.target.value })}
                        min="1"
                        required
                      />
                      <p className="text-xs text-muted-foreground">Days to receive after ordering (required)</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Source</Label>
                      <Select value={formData.source_type} onValueChange={(val) => setFormData({ ...formData, source_type: val })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MP">MP (Market Purchase)</SelectItem>
                          <SelectItem value="IP">IP (In-house Production)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">MP = buy, IP = manufacture</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Default Location {formData.source_type === 'MP' && <span className="text-red-500">*</span>}</Label>
                      <Select value={formData.default_location_id || 'none'} onValueChange={(val) => setFormData({ ...formData, default_location_id: val === 'none' ? '' : val, sub_location_id: '' })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select location..." />
                        </SelectTrigger>
                        <SelectContent>
                          {formData.source_type !== 'MP' && <SelectItem value="none">-- Not Specified --</SelectItem>}
                          {locations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.code} - {loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {formData.source_type === 'MP' ? 'Required for MP items - determines Purchase team assignment' : 'Default storage/production location'}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Sub-Location</Label>
                      <Select 
                        value={formData.sub_location_id || 'none'} 
                        onValueChange={(val) => setFormData({ ...formData, sub_location_id: val === 'none' ? '' : val })}
                        disabled={!formData.default_location_id}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={formData.default_location_id ? "Select sub-location..." : "Select location first"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Not Specified --</SelectItem>
                          {subLocations
                            .filter(sl => !formData.default_location_id || sl.parent_location_id === formData.default_location_id)
                            .map(sl => (
                              <SelectItem key={sl.id} value={sl.id}>{sl.code} - {sl.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">Exact storage position (Rack, Shelf)</p>
                    </div>
                    
                    {/* Description */}
                    <div className="col-span-3 space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Item description..."
                        rows={2}
                      />
                    </div>
                    
                    {/* Specifications - Multi-line input */}
                    <div className="col-span-3 space-y-2">
                      <Label>Specifications</Label>
                      <Textarea
                        value={formData.specs}
                        onChange={(e) => setFormData({ ...formData, specs: e.target.value })}
                        placeholder="Enter specifications exactly as needed.&#10;Copy-paste from any source - formatting will be preserved.&#10;&#10;Example:&#10;Fixed Buffet Rack&#10;1) Support Frame: Material - MS square tube&#10;2) Panel: 18mm MDF with laminate"
                        rows={6}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Paste specifications exactly as needed. Line breaks and formatting will be preserved. Text will be converted to UPPERCASE on save.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit">{editItem ? 'Update' : 'Create'} Item</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
              )}
            </Dialog>
          </div>
        </div>

        {/* Category Tabs and Search */}
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList>
              <TabsTrigger value="all">All ({items.length})</TabsTrigger>
              <TabsTrigger value="RMT">RMT ({items.filter(i => i.category?.toUpperCase() === 'RMT').length})</TabsTrigger>
              <TabsTrigger value="FNG">FNG ({items.filter(i => i.category?.toUpperCase() === 'FNG').length})</TabsTrigger>
              <TabsTrigger value="SFG">SFG ({items.filter(i => i.category?.toUpperCase() === 'SFG').length})</TabsTrigger>
              <TabsTrigger value="CTM">CTM ({items.filter(i => i.category?.toUpperCase() === 'CTM').length})</TabsTrigger>
              <TabsTrigger value="ASSET">ASSET ({items.filter(i => i.category?.toUpperCase() === 'ASSET').length})</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Need Filter */}
            <Select value={needFilter} onValueChange={setNeedFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Need" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Needs</SelectItem>
                <SelectItem value="APO">
                  <span className="flex items-center gap-1">
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">APO</Badge>
                  </span>
                </SelectItem>
                <SelectItem value="REG">
                  <span className="flex items-center gap-1">
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">REG</Badge>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            
            {/* Source Filter */}
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="MP">
                  <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> MP (Buy)</span>
                </SelectItem>
                <SelectItem value="IP">
                  <span className="flex items-center gap-1"><Factory className="w-3 h-3" /> IP (Make)</span>
                </SelectItem>
              </SelectContent>
            </Select>
            
            {/* Location Filter */}
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.code} - {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search items..."
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {/* Items Master Table - Static attributes only */}
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-center">Need</TableHead>
                <TableHead className="text-center">Source</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Sub-Location</TableHead>
                <TableHead className="text-center">Lead Time</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No items found</p>
                    <p className="text-sm text-muted-foreground mt-1">Create items or adjust filters</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => {
                  const isExpanded = expandedItem === item.id;
                  
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow className="hover:bg-muted/30">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-6 w-6"
                            onClick={() => toggleExpand(item.id)}
                          >
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{item.name}</span>
                          {item.category === 'CTM' && (
                            <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-200 text-xs">CTM</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={categoryColors[item.category]}>
                            {categoryLabels[item.category] || item.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={needTypeColors[item.need_type || 'REG']} variant="outline">
                            {item.need_type || 'REG'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {sourceTypeIcons[item.source_type || 'MP']}
                            <span className="text-sm">{item.source_type || 'MP'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.default_location_id ? (
                            <span className="text-sm">
                              {locations.find(l => l.id === item.default_location_id)?.code || 
                               locations.find(l => l.id === item.default_location_id)?.name || 
                               '-'}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.sub_location ? (
                            <Badge variant="outline" className="font-mono text-xs">
                              {item.sub_location}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.lead_time ? (
                            <div className="flex items-center justify-center gap-1">
                              <Clock className="w-3 h-3 text-muted-foreground" />
                              <span>{item.lead_time}d</span>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{item.unit || '-'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(item)} title="Delete">
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Row - Item Details */}
                      {isExpanded && (
                        <TableRow className="bg-muted/20">
                          <TableCell></TableCell>
                          <TableCell colSpan={9}>
                            <div className="py-3 space-y-3">
                              <div className="grid grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Classification</p>
                                  <p className="font-medium">{item.classification || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Price</p>
                                  <p className="font-medium">₹{(item.price || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Min Stock Level</p>
                                  <p className="font-medium">{item.min_stock_level || 0}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Reorder Qty</p>
                                  <p className="font-medium">{item.reorder_quantity || '-'}</p>
                                </div>
                              </div>
                              {item.description && (
                                <div className="border-t pt-2">
                                  <p className="text-muted-foreground text-sm mb-1">Description:</p>
                                  <p className="text-sm">{item.description}</p>
                                </div>
                              )}
                              {item.specs && (typeof item.specs === 'string' ? item.specs.trim() : Object.keys(item.specs).length > 0) && (
                                <div className="border-t pt-2">
                                  <p className="text-muted-foreground text-sm mb-1">Specifications:</p>
                                  <pre className="text-gray-700 whitespace-pre-wrap font-mono text-xs bg-gray-50 p-2 rounded">
                                    {typeof item.specs === 'string' ? item.specs : normalizeSpecs(item.specs)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDelete} onOpenChange={setOpenDelete}>
          {openDelete && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Delete Item
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{selectedItem?.name}&quot;? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete}>Delete</Button>
            </DialogFooter>
          </DialogContent>
          )}
        </Dialog>
        
        {/* Classification Management Dialog */}
        <Dialog open={openClassificationDialog} onOpenChange={setOpenClassificationDialog}>
          {openClassificationDialog && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Manage Classifications</DialogTitle>
              <DialogDescription>
                Add or remove classification options for FNG items
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Search existing classifications */}
              <Input
                placeholder="Search classifications..."
                value={classificationSearch || ''}
                onChange={(e) => setClassificationSearch(e.target.value)}
                className="mb-2"
              />
              {/* Add new classification */}
              <div className="flex gap-2">
                <Input
                  placeholder="New classification..."
                  value={newClassification}
                  onChange={(e) => setNewClassification(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newClassification.trim()) {
                      const updated = [...classifications, newClassification.trim()].sort((a, b) => a.localeCompare(b));
                      setClassifications(updated);
                      setNewClassification('');
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={() => {
                    if (newClassification.trim()) {
                      const updated = [...classifications, newClassification.trim()].sort((a, b) => a.localeCompare(b));
                      setClassifications(updated);
                      setNewClassification('');
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {classifications.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4 text-center">No classifications defined</p>
                ) : (
                  <ul className="divide-y">
                    {[...classifications]
                      .sort((a, b) => a.localeCompare(b))
                      .filter(c => !classificationSearch || c.toLowerCase().includes(classificationSearch.toLowerCase()))
                      .map((c, idx) => (
                      <li key={idx} className="flex items-center justify-between px-3 py-2">
                        <span>{c}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          onClick={() => {
                            const updated = classifications.filter(cls => cls !== c);
                            setClassifications(updated);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenClassificationDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await axios.post(`${API_URL}/items/classifications`, 
                      { classifications }, 
                      { headers: getAuthHeader() }
                    );
                    toast.success('Classifications saved');
                    setOpenClassificationDialog(false);
                  } catch (error) {
                    toast.error('Failed to save classifications');
                  }
                }}
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
          )}
        </Dialog>

        {/* Code Library Picker Modal */}
        <Dialog open={codePickerOpen} onOpenChange={setCodePickerOpen}>
          {codePickerOpen && (
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Library className="w-5 h-5" />
                Import from Code Library
              </DialogTitle>
              <DialogDescription>
                Select a generated code to import into Item Master. Name and specs will be auto-filled.
              </DialogDescription>
            </DialogHeader>
            
            {/* Search */}
            <div className="flex gap-2 py-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code, product, or specs..."
                  value={codePickerSearch}
                  onChange={(e) => {
                    setCodePickerSearch(e.target.value);
                    fetchAvailableCodes(e.target.value);
                  }}
                  className="pl-9"
                />
              </div>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => fetchAvailableCodes(codePickerSearch)}
                disabled={codePickerLoading}
              >
                {codePickerLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>

            {/* Codes List */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {codePickerLoading ? (
                <div className="p-8 text-center text-muted-foreground">Loading available codes...</div>
              ) : availableCodes.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Library className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No available codes found</p>
                  <p className="text-xs mt-1">All generated codes may already be imported, or none exist yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-[100px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableCodes.map((code) => (
                      <TableRow key={code.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-mono text-sm font-medium">{code.code}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2">{code.specs}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{code.generic_name || code.code_product_name}</Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleImportCode(code)}
                          >
                            Import
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setCodePickerOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
          )}
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
